import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";

function formatDate(dateValue) {
  if (!dateValue) return "Not applied yet";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "Invalid date";
  return parsed.toLocaleString();
}

function formatAmount(amount, amountType, currency) {
  const numericValue = Number(amount);
  if (!Number.isFinite(numericValue)) return "-";
  if (amountType === "relative") return `${numericValue}%`;
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: currency || "EUR" }).format(numericValue);
  } catch (_error) {
    return `${numericValue} ${currency || ""}`.trim();
  }
}

function formatValidity(row) {
  if (!row.valid_from && !row.valid_to) return "Always available";
  if (row.valid_from && row.valid_to) return `${row.valid_from} → ${row.valid_to}`;
  if (row.valid_from) return `From ${row.valid_from}`;
  return `Until ${row.valid_to}`;
}

function formatChargeSummary(row) {
  return [row.scope, row.charge_period, row.scope === "per_reservation" ? "per_room" : row.charge_unit]
    .filter(Boolean)
    .join(" · ");
}

function formatRestrictions(row) {
  const parts = [];
  if (row.min_nights !== null && typeof row.min_nights !== "undefined") parts.push(`Min nights ${row.min_nights}`);
  if (row.max_nights !== null && typeof row.max_nights !== "undefined") parts.push(`Max nights ${row.max_nights}`);
  if (row.min_pax !== null && typeof row.min_pax !== "undefined") parts.push(`Min pax ${row.min_pax}`);
  if (row.max_pax !== null && typeof row.max_pax !== "undefined") parts.push(`Max pax ${row.max_pax}`);
  return parts.length ? parts.join(" · ") : "No restrictions";
}

export default function OnboardingExtrasDiscountsAdmin() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applyingBusinessUnitId, setApplyingBusinessUnitId] = useState(null);
  const [feedback, setFeedback] = useState("");

  const selectedRows = useMemo(
    () => (Array.isArray(selectedDraft?.rows) ? selectedDraft.rows : []),
    [selectedDraft?.rows]
  );
  const categoryMap = useMemo(
    () =>
      new Map(
        (Array.isArray(selectedDraft?.references?.room_categories) ? selectedDraft.references.room_categories : []).map((row) => [
          Number(row.id),
          row,
        ])
      ),
    [selectedDraft?.references?.room_categories]
  );
  const rateMap = useMemo(
    () =>
      new Map(
        (Array.isArray(selectedDraft?.references?.room_rates) ? selectedDraft.references.room_rates : []).map((row) => [
          Number(row.id),
          row,
        ])
      ),
    [selectedDraft?.references?.room_rates]
  );
  const selectedSummary = useMemo(
    () => ({
      total: selectedRows.length,
      active: selectedRows.filter((row) => row.is_active !== false).length,
      automatic: selectedRows.filter((row) => row.apply_automatically).length,
      mandatory: selectedRows.filter((row) => row.is_mandatory).length,
    }),
    [selectedRows]
  );

  const loadDrafts = async () => {
    setLoading(true);
    setFeedback("");

    try {
      const response = await axios.get("/api/onboarding/admin/extras-discounts/drafts");
      setDrafts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("[Admin][Onboarding][ExtrasDiscounts] Error loading drafts:", error);
      setFeedback("Could not load onboarding extras and discounts drafts.");
    } finally {
      setLoading(false);
    }
  };

  const loadDraftDetail = async (businessUnitId) => {
    setSelectedBusinessUnitId(businessUnitId);
    setDetailLoading(true);
    setFeedback("");

    try {
      const response = await axios.get(`/api/onboarding/admin/extras-discounts/${businessUnitId}`);
      setSelectedDraft(response.data);
    } catch (error) {
      console.error("[Admin][Onboarding][ExtrasDiscounts] Error loading draft detail:", error);
      setSelectedDraft(null);
      setFeedback("Could not load the selected extras and discounts draft.");
    } finally {
      setDetailLoading(false);
    }
  };

  const applyDraft = async (businessUnitId, businessUnitName) => {
    const confirmed = window.confirm(
      `Apply the onboarding extras and discounts draft for ${businessUnitName}? This will create or update rows in extras.`
    );

    if (!confirmed) return;

    setApplyingBusinessUnitId(businessUnitId);
    setFeedback("");

    try {
      const response = await axios.post(`/api/onboarding/admin/extras-discounts/${businessUnitId}/apply`);
      const result = response.data?.result || {};
      setFeedback(
        `Draft applied for ${businessUnitName}. Created: ${result.created || 0}. Updated: ${result.updated || 0}.`
      );
      await loadDrafts();
      await loadDraftDetail(businessUnitId);
    } catch (error) {
      console.error("[Admin][Onboarding][ExtrasDiscounts] Error applying draft:", error);
      setFeedback(error?.response?.data?.error || "Could not apply the selected extras and discounts draft.");
    } finally {
      setApplyingBusinessUnitId(null);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  return (
    <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px" }}>
      <div
        style={{
          borderRadius: "22px",
          padding: "22px",
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "radial-gradient(780px 240px at 0% 0%, rgba(92,191,255,0.12), transparent 60%), rgba(10,15,28,0.9)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.12em", color: "#8ea0bc" }}>
          Admin apply area
        </div>
        <h2 style={{ color: "#fff", margin: "8px 0 10px" }}>Onboarding Extras & Discounts</h2>
        <p style={{ color: "#b9c3d7", maxWidth: "940px", margin: 0, lineHeight: 1.6 }}>
          Extras and discounts created in onboarding stay in staging until an admin validates them here and applies
          them to <strong>`extras`</strong>. Nothing from this step goes live automatically.
        </p>
      </div>

      {feedback ? (
        <div
          style={{
            borderRadius: "14px",
            padding: "12px 14px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: feedback.toLowerCase().includes("could not") || feedback.toLowerCase().includes("must")
              ? "#542929"
              : "#244428",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {feedback}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 420px) minmax(0, 1fr)", gap: "18px" }}>
        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
            <div>
              <h3 style={{ color: "#fff", margin: 0 }}>Drafts</h3>
              <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                Business units with staged extras and discounts.
              </p>
            </div>
            <button
              type="button"
              onClick={loadDrafts}
              disabled={loading}
              style={{
                borderRadius: "12px",
                border: "none",
                padding: "10px 14px",
                background: "#5cbfff",
                color: "#08111e",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {loading ? (
              <div style={{ color: "#9db0cf" }}>Loading extras drafts...</div>
            ) : drafts.length === 0 ? (
              <div style={{ color: "#9db0cf" }}>No extras and discounts drafts available yet.</div>
            ) : (
              drafts.map((draft) => {
                const isSelected = Number(selectedBusinessUnitId) === Number(draft.business_unit_id);
                return (
                  <article
                    key={draft.business_unit_id}
                    style={{
                      borderRadius: "18px",
                      padding: "16px",
                      border: `1px solid ${isSelected ? "rgba(92,191,255,0.45)" : "rgba(255,255,255,0.08)"}`,
                      background: isSelected ? "rgba(92,191,255,0.08)" : "rgba(255,255,255,0.03)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                      <div>
                        <h4 style={{ color: "#fff", margin: 0 }}>{draft.business_unit_name}</h4>
                        <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>{draft.rows_total} staged rows</p>
                      </div>
                      <div style={{ color: "#e7edf8", fontWeight: 700 }}>{draft.linked_rows_total} linked</div>
                    </div>

                    <div style={{ marginTop: "12px", color: "#9db0cf", fontSize: "0.9rem", lineHeight: 1.5 }}>
                      <div>Last save: {formatDate(draft.updated_at)}</div>
                      <div>Last apply: {formatDate(draft.applied_at)}</div>
                    </div>

                    <div style={{ marginTop: "14px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => loadDraftDetail(draft.business_unit_id)}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#1b2c43",
                          color: "#fff",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => applyDraft(draft.business_unit_id, draft.business_unit_name)}
                        disabled={applyingBusinessUnitId === draft.business_unit_id}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#ff975c",
                          color: "#08111e",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {applyingBusinessUnitId === draft.business_unit_id ? "Applying..." : "Apply to live"}
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          {!selectedBusinessUnitId ? (
            <div style={{ color: "#9db0cf" }}>Select a draft to preview its staged extras and discounts.</div>
          ) : detailLoading ? (
            <div style={{ color: "#9db0cf" }}>Loading draft detail...</div>
          ) : !selectedDraft ? (
            <div style={{ color: "#9db0cf" }}>No draft detail loaded.</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", flexWrap: "wrap", marginBottom: "16px" }}>
                <div>
                  <h3 style={{ color: "#fff", margin: 0 }}>{selectedDraft.session?.business_unit_name}</h3>
                  <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                    Last save: {formatDate(selectedDraft.session?.updated_at)}. Last apply:{" "}
                    {formatDate(selectedDraft.session?.applied_at)}.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    applyDraft(selectedDraft.session.business_unit_id, selectedDraft.session.business_unit_name)
                  }
                  disabled={applyingBusinessUnitId === selectedDraft.session?.business_unit_id}
                  style={{
                    borderRadius: "12px",
                    border: "none",
                    padding: "12px 16px",
                    background: "#5cbfff",
                    color: "#08111e",
                    fontWeight: 800,
                    cursor: "pointer",
                    opacity: applyingBusinessUnitId === selectedDraft.session?.business_unit_id ? 0.7 : 1,
                  }}
                >
                  {applyingBusinessUnitId === selectedDraft.session?.business_unit_id ? "Applying..." : "Apply draft"}
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                {[
                  ["Rows", selectedSummary.total],
                  ["Active", selectedSummary.active],
                  ["Automatic", selectedSummary.automatic],
                  ["Mandatory", selectedSummary.mandatory],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "14px",
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.03)",
                      padding: "14px",
                    }}
                  >
                    <div style={{ color: "#8ea0bc", fontSize: "0.82rem" }}>{label}</div>
                    <div style={{ color: "#fff", fontSize: "1.5rem", fontWeight: 800, marginTop: "6px" }}>{value}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gap: "12px" }}>
                {selectedRows.length === 0 ? (
                  <div style={{ color: "#9db0cf" }}>No extras or discounts staged.</div>
                ) : (
                  selectedRows.map((row) => {
                    const categoryNames = (Array.isArray(row.room_category_ids) ? row.room_category_ids : [])
                      .map((id) => categoryMap.get(Number(id))?.name || null)
                      .filter(Boolean);
                    const rateNames = (Array.isArray(row.room_rate_ids) ? row.room_rate_ids : [])
                      .map((id) => {
                        const rate = rateMap.get(Number(id));
                        if (!rate) return null;
                        return rate.rate_code ? `${rate.rate_code} · ${rate.name}` : rate.name;
                      })
                      .filter(Boolean);
                    const flags = [];
                    if (row.inclusive) flags.push("Inclusive");
                    if (row.apply_automatically) flags.push("Automatic");
                    if (row.is_mandatory) flags.push("Mandatory");
                    if (row.is_active === false) flags.push("Inactive");

                    return (
                      <article
                        key={row.id}
                        style={{
                          borderRadius: "16px",
                          border: "1px solid rgba(255,255,255,0.08)",
                          background: "rgba(255,255,255,0.03)",
                          padding: "16px",
                          opacity: row.is_active === false ? 0.7 : 1,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                          <div>
                            <h4 style={{ color: "#fff", margin: 0 }}>{row.name}</h4>
                            <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>{row.description || "No description"}</p>
                          </div>
                          <div style={{ color: "#fff", fontWeight: 700 }}>{formatAmount(row.amount, row.amount_type, row.currency)}</div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", marginTop: "14px" }}>
                          <div>
                            <div style={{ color: "#8ea0bc", fontSize: "0.8rem" }}>Charge logic</div>
                            <div style={{ color: "#fff", marginTop: "4px" }}>{formatChargeSummary(row)}</div>
                          </div>
                          <div>
                            <div style={{ color: "#8ea0bc", fontSize: "0.8rem" }}>Validity</div>
                            <div style={{ color: "#fff", marginTop: "4px" }}>{formatValidity(row)}</div>
                          </div>
                          <div>
                            <div style={{ color: "#8ea0bc", fontSize: "0.8rem" }}>Restrictions</div>
                            <div style={{ color: "#fff", marginTop: "4px" }}>{formatRestrictions(row)}</div>
                          </div>
                          <div>
                            <div style={{ color: "#8ea0bc", fontSize: "0.8rem" }}>Live row</div>
                            <div style={{ color: "#fff", marginTop: "4px" }}>{row.applied_extra_id || "-"}</div>
                          </div>
                        </div>

                        <div style={{ marginTop: "14px", color: "#c7d2e7", lineHeight: 1.6 }}>
                          <div>Room categories: {categoryNames.length ? categoryNames.join(", ") : "All staged categories"}</div>
                          <div>Room rates: {rateNames.length ? rateNames.join(", ") : "No rate filter"}</div>
                          <div>Flags: {flags.length ? flags.join(", ") : "None"}</div>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
